/** One AI run in full (plan §3.3.3): the effective settings it ran with and where each came from,
 *  status, trigger, cost, model, hashes and its plan — and, compared with another run of the same
 *  product (`?compare=`, diffed server-side), which settings and facts changed between them. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as catalogueApi from '@/api/catalogue';
import { catalogueKeys, type CatalogueRegistry, type SettingValue } from '@/api/catalogue';
import { AppDialog } from '@/components/AppDialog';
import { DocumentViewer } from '@/components/DocumentViewer';
import { formatDate } from '@/utils/format';

import { formatCost, sourceLabel, specsByKey, syncStatusTone, valueLabel } from './catalogueModel';
import { triggerLabel } from './RunsTable';
import { SourceChip } from './SettingControl';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function plain(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

export function RunDetailDialog({
  tenantId,
  runId,
  registry,
  onClose,
}: {
  tenantId: number;
  runId: number;
  registry: CatalogueRegistry;
  onClose: () => void;
}) {
  const specs = specsByKey(registry);
  const [compare, setCompare] = useState<number | null>(null);
  const [showPlan, setShowPlan] = useState(false);
  const run = useQuery({
    queryKey: catalogueKeys.run(tenantId, runId, compare),
    queryFn: ({ signal }) => catalogueApi.getRun(tenantId, runId, compare, signal),
    retry: false,
  });
  const productId = run.data?.product_id;
  const siblings = useQuery({
    queryKey: catalogueKeys.runs(tenantId, { product_id: productId, page_size: 50, siblings: true }),
    queryFn: ({ signal }) => catalogueApi.listRuns(tenantId, { product_id: productId, page_size: 50 }, signal),
    enabled: productId != null,
    retry: false,
  });
  const r = run.data;
  const settingsWord = (key: string, v: SettingValue | null | undefined) =>
    v ? `${valueLabel(specs.get(key), v.value)} · ${sourceLabel(v.source)}` : '—';

  const rowsOf = () => {
    const out: Array<{ key: string; option: string | null; v: SettingValue }> = [];
    const eff = r?.effective_settings;
    if (!eff) return out;
    for (const [key, v] of Object.entries(eff.product ?? {})) out.push({ key, option: null, v });
    for (const [option, values] of Object.entries(eff.options ?? {})) {
      for (const [key, v] of Object.entries(values ?? {})) out.push({ key, option, v });
    }
    return out;
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      size="lg"
      title={r ? `Run #${r.id} · ${r.product?.name ?? ''}` : `Run #${runId}`}
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {run.isLoading && <CircularProgress size={24} aria-label="Loading the run" />}
      {run.isError && <Alert severity="error">{(run.error as Error).message}</Alert>}
      {r && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Fact label="When">{formatDate(r.created_ts)}</Fact>
            <Fact label="Status">
              <Chip size="small" label={r.status} color={syncStatusTone(r.status)} variant="outlined" sx={{ height: 20 }} />
              {r.accepted ? ' · imported' : ' · not imported'}
            </Fact>
            <Fact label="Trigger">{triggerLabel(r.trigger)}</Fact>
            <Fact label="Stage">{r.stage || '—'}</Fact>
            <Fact label="Cost">{formatCost(r.cost_usd)}</Fact>
            <Fact label="Model">{r.model || '—'}</Fact>
            <Fact label="Plan version">{r.plan_version || '—'}</Fact>
            <Fact label="Task">{r.bgtask ? <RouterLink to={`/bgtasks/${r.bgtask}`}>open</RouterLink> : '—'}</Fact>
            <Fact label="Input / settings hash">
              <Box component="span" sx={{ fontFamily: MONO, fontSize: 12 }}>
                {r.input_hash.slice(0, 12) || '—'} / {r.settings_hash.slice(0, 12) || '—'}
              </Box>
            </Fact>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Compare with"
              value={compare ?? ''}
              onChange={(e) => setCompare(e.target.value ? Number(e.target.value) : null)}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="">No comparison</MenuItem>
              {(siblings.data?.results ?? [])
                .filter((s) => s.id !== r.id)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    #{s.id} · {formatDate(s.created_ts)} · {triggerLabel(s.trigger)} · {s.status}
                  </MenuItem>
                ))}
            </TextField>
            {compare != null && r.compare && (
              <Typography variant="body2" color="text.secondary">
                {r.compare.settings_diff.length} setting(s) and {r.compare.facts_diff.length} fact(s) differ
                (before = run #{r.compare.run.id}, after = this run)
              </Typography>
            )}
          </Box>

          {compare != null && r.compare && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Settings diff
              </Typography>
              {r.compare.settings_diff.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Same settings, same sources.
                </Typography>
              ) : (
                <Table size="small" aria-label="Settings diff">
                  <TableHead>
                    <TableRow>
                      <TableCell>Setting</TableCell>
                      <TableCell>Option</TableCell>
                      <TableCell>Before</TableCell>
                      <TableCell>After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {r.compare.settings_diff.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{specs.get(d.key)?.label ?? d.key}</TableCell>
                        <TableCell sx={{ fontFamily: MONO, fontSize: 12 }}>{d.option_id ?? '—'}</TableCell>
                        <TableCell>{settingsWord(d.key, d.before)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{settingsWord(d.key, d.after)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {r.compare.facts_diff.length > 0 && (
                <Table size="small" aria-label="Facts diff" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fact</TableCell>
                      <TableCell>Before</TableCell>
                      <TableCell>After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {r.compare.facts_diff.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: 12 }}>{d.key}</TableCell>
                        <TableCell>{plain(d.before)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{plain(d.after)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Effective settings it ran with
            </Typography>
            <Table size="small" aria-label="Effective settings">
              <TableHead>
                <TableRow>
                  <TableCell>Setting</TableCell>
                  <TableCell>Option</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsOf().map(({ key, option, v }) => (
                  <TableRow key={`${option ?? ''}:${key}`}>
                    <TableCell>{specs.get(key)?.label ?? key}</TableCell>
                    <TableCell sx={{ fontFamily: MONO, fontSize: 12 }}>{option ?? '—'}</TableCell>
                    <TableCell>{valueLabel(specs.get(key), v.value)}</TableCell>
                    <TableCell>
                      <SourceChip source={v.source} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {Object.keys(r.facts).length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Facts
              </Typography>
              <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.25 }}>
                {Object.entries(r.facts).map(([k, v]) => (
                  <Box key={k} sx={{ display: 'contents' }}>
                    <Box component="dt" sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary' }}>
                      {k}
                    </Box>
                    <Box component="dd" sx={{ m: 0, typography: 'body2', wordBreak: 'break-word' }}>
                      {plain(v)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {r.output && (
            <Box>
              <Button size="small" onClick={() => setShowPlan((v) => !v)}>
                {showPlan ? 'Hide the plan' : 'Show the plan it produced'}
              </Button>
              {showPlan && (
                <Box sx={{ mt: 1 }}>
                  <DocumentViewer
                    text={JSON.stringify(r.output)}
                    formatHint="json"
                    downloadName={`catalogue-run-${r.id}`}
                    copyMessage="Plan copied."
                    emptyMessage="No plan."
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </AppDialog>
  );
}
